import * as React from "react";
import { cn } from "@/lib/utils";

export type HeroVariant = "standard" | "compact" | "learning" | "technical" | "creative" | "gamified";
export type Category = "safety" | "prompting" | "cyber" | "fluency" | "creative" | "video" | "thinking";

interface PageHeroProps {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  visual?: React.ReactNode;
  variant?: HeroVariant;
  /** Optional category accent tints the eyebrow + atmosphere; brand yellow is the default. */
  category?: Category;
  className?: string;
  children?: React.ReactNode;
}

/** One GenieBot hero language: eyebrow, strong H1, plain copy, 1–2 actions, subtle atmosphere. */
export const PageHero = ({
  eyebrow,
  title,
  description,
  actions,
  visual,
  variant = "standard",
  category,
  className,
  children,
}: PageHeroProps) => {
  const accent = category ? `var(--category-${category})` : "var(--primary)";
  const compact = variant === "compact";
  return (
    <header
      className={cn("relative isolate overflow-hidden", compact ? "py-8 sm:py-10" : "py-12 sm:py-16", className)}
      style={{ ["--hero-accent" as string]: accent }}
      data-hero-variant={variant}
    >
      {/* Subtle atmosphere — a single radial wash, no blur/particles */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 opacity-60"
        style={{ background: "radial-gradient(60% 50% at 50% 0%, hsl(var(--hero-accent) / 0.12), transparent 70%)" }}
      />
      <div className={cn("mx-auto max-w-7xl px-4 sm:px-6", visual && "grid items-center gap-8 lg:grid-cols-2")}>
        <div className={cn(!visual && "max-w-3xl")}>
          {eyebrow && <SectionEyebrow>{eyebrow}</SectionEyebrow>}
          <h1
            className={cn(
              "font-bold tracking-tight text-foreground text-balance",
              compact ? "text-2xl sm:text-3xl" : "text-3xl sm:text-4xl lg:text-5xl",
            )}
          >
            {title}
          </h1>
          {description && <p className="mt-4 max-w-2xl text-base sm:text-lg text-muted-foreground">{description}</p>}
          {actions && <div className="mt-6 flex flex-wrap gap-3">{actions}</div>}
          {children}
        </div>
        {visual && <div className="min-w-0">{visual}</div>}
      </div>
    </header>
  );
};

export const SectionEyebrow = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <p
    className={cn(
      "mb-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider [color:hsl(var(--hero-accent,var(--primary)))]",
      className,
    )}
  >
    {children}
  </p>
);

interface SectionHeaderProps {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  as?: "h2" | "h3";
  align?: "left" | "center";
  className?: string;
  id?: string;
}

export const SectionHeader = ({ eyebrow, title, description, action, as: H = "h2", align = "left", className, id }: SectionHeaderProps) => (
  <div
    className={cn(
      "mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between",
      align === "center" && "items-center text-center sm:flex-col sm:items-center",
      className,
    )}
  >
    <div className={cn(align === "center" && "mx-auto max-w-2xl")}>
      {eyebrow && <SectionEyebrow>{eyebrow}</SectionEyebrow>}
      <H id={id} className={cn("font-bold tracking-tight text-foreground", H === "h2" ? "text-2xl sm:text-3xl" : "text-xl")}>
        {title}
      </H>
      {description && <p className="mt-2 text-muted-foreground">{description}</p>}
    </div>
    {action && <div className="shrink-0">{action}</div>}
  </div>
);

export const CategoryBadge = ({ category, children, className }: { category: Category; children: React.ReactNode; className?: string }) => (
  <span
    className={cn(
      "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium",
      "[border-color:hsl(var(--cat)/0.4)] [background-color:hsl(var(--cat)/0.1)] text-foreground",
      className,
    )}
    style={{ ["--cat" as string]: `var(--category-${category})` }}
  >
    <span aria-hidden className="h-1.5 w-1.5 rounded-full [background-color:hsl(var(--cat))]" />
    {children}
  </span>
);
